import { mainLayoutConstants } from '@/lib/main-layout-constants';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Box, IconButton, Stack, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import type { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MuiAppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useObservable } from 'react-use';
import { useMainLayoutContext } from '../../MainLayoutContext';
import SceneTabs from '../SceneTabs';
import SideSceneTabs from '../SideSceneTabs';
const { sidemenu, content } = mainLayoutConstants;
type DialogId = 'PageListDialog';

interface Props {
  isSidebarOpen: boolean;
  className?: string;
  onClickMenuButton: () => void;
}

// const drawerWidth = 300
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: useMediaQuery(theme.breakpoints.down('md'))
    ? theme.zIndex.drawer
    : theme.zIndex.drawer + 1,
  backgroundColor: '#fff',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: useMediaQuery(theme.breakpoints.down('md')) ? 0 : sidemenu.closeWidth,
  width: useMediaQuery(theme.breakpoints.down('md'))
    ? '100%'
    : `calc(100% - ${sidemenu.closeWidth}px)`,
  ...(open && {
    marginLeft: sidemenu.openWidth,
    width: `calc(100% - ${sidemenu.openWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: '0.2s',
    }),
  }),
}));

export default function SideIsTopbar(props: Props) {
  const { className, isSidebarOpen, onClickMenuButton } = props;
  const { sceneManager } = useMainLayoutContext();
  const theme = useTheme();
  const smOrDown = useMediaQuery(theme.breakpoints.down('md'));
  const rootRef = useRef<HTMLDivElement | null>(null);
  const shift = !!(isSidebarOpen && !smOrDown);
  const currentPage = useObservable(sceneManager.observeCurrentSceneOrNull(), null);

  // 페이지 번호입력 텍스트 필드 포커스
  useHotkeys('alt+shift+k', (event) => {
    const root = rootRef.current;
    if (!root) return;
    const input = root.querySelector<HTMLInputElement>('.SideIsTopbar-pageKeyTextField input');
    input?.focus();
  });

  // 현재 페이지 닫기
  useHotkeys('alt+shift+x', (event) => {
    if (!currentPage) return;
    sceneManager.deleteBySceneId(currentPage?.sceneId);
  });

  return (
    <AppBar
      className={clsx('SideIsTopbar-root', className)}
      position="fixed"
      open={shift && !smOrDown}
      sx={{
        height: 40,
        backgroundColor: '#fff',
        boxShadow: 'none',
        borderBottom: '1px solid #e0e0e0',
      }}
      elevation={1}
      ref={rootRef}
    >
      <Toolbar
        sx={{
          whiteSpace: 'nowrap',
          '&.MuiToolbar-regular': {
            minHeight: 40,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          sx={{ width: '100%', height: '100%' }}
        >
          <IconButton
            onClick={onClickMenuButton}
            aria-label="open drawer"
            edge="start"
            size="small"
            sx={{
              flex: 0,
              color: '#000',
            }}
          >
            {isSidebarOpen ? (
              <MenuOpenIcon fontSize="small" htmlColor="#000" />
            ) : (
              <MenuIcon fontSize="small" htmlColor="#000" />
            )}
          </IconButton>
          <Box
            sx={{
              flex: 1,
              pl: 1,
              display: 'flex',
              height: '100%',
              alignItems: 'self-end',
            }}
          >
            {sceneManager.count() > 0 && <SideSceneTabs sx={{ flex: 1, width: 0 }} />}
          </Box>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
